import { forEach } from 'lodash';
import { getTranslatedKPINameV2, translate } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';
import { getMeasurementUnitSymbol } from 'helpers/unitHelper';
import { chartHeightConstants } from '../../../constants';
import {
    CHART_CONDITION_TREND_KPI_TYPE,
    statusBarColors,
    maxAnomalyLevel,
} from '../CoolingAnomalyChart/constants';


export const PRINT_FONT_FAMILY = 'ABBvoice-Regular';

const SIGNAL_MARKER_SYMBOLS_FALLBACK = ['diamond', 'triangle-down', 'circle', 'square', 'triangle'];
const getACS1000MarkerSymbol = (signalKey = '', fallbackIndex = 0) => {
    const key = signalKey.toLowerCase();
    if (key.includes('conductivity')) { return 'circle'; }
    if (key.includes('temperature')) { return 'square'; }
    if (key.includes('pressure')) { return 'triangle'; }
    return SIGNAL_MARKER_SYMBOLS_FALLBACK[fallbackIndex % SIGNAL_MARKER_SYMBOLS_FALLBACK.length];
};

const ACS1000_SIGNAL_COLORS = {
    conductivity: colors.coolingAnomaly,
    temperature: colors.coolingTemperature,
    pressure: colors.coolingPressure,
};

const ANOMALY_POINT_COLOR = colors.orangePeel;


const getACS1000SignalColor = (signalKey = '') => {
    const key = signalKey.toLowerCase();
    if (key.includes('conductivity')) { return ACS1000_SIGNAL_COLORS.conductivity; }
    if (key.includes('temperature')) { return ACS1000_SIGNAL_COLORS.temperature; }
    if (key.includes('pressure')) { return ACS1000_SIGNAL_COLORS.pressure; }
    return colors.cerulean;
};


const getLastAnomalyLevel = (values = []) => {
    for (let i = values.length - 1; i >= 0; i--) {
        const level = values[i]?.anomalyLevel;
        if (level !== null && level !== undefined) { return level; }
    }
    return 0;
};

export const getACS1000AnomalySignalDescriptions = (conditionTrendsData) => {
    if (!conditionTrendsData?.length) { return []; }
    return conditionTrendsData
        .filter(item => item.type !== CHART_CONDITION_TREND_KPI_TYPE)
        .filter(item => getLastAnomalyLevel(item?.values) > 0)
        .map(item => getTranslatedKPINameV2(item));
};

export const getMaxSignalAnomalyLevel = (conditionTrendsData) => {
    if (!conditionTrendsData?.length) { return 0; }
    return conditionTrendsData
        .filter(item => item?.type !== CHART_CONDITION_TREND_KPI_TYPE)
        .reduce((max, item) => Math.max(max, getLastAnomalyLevel(item?.values)), 0);
};

export const getWorstACS1000AnomalySignalDescriptions = (conditionTrendsData) => {
    if (!conditionTrendsData?.length) { return []; }

    const signalLevels = conditionTrendsData
        .filter(item => item.type !== CHART_CONDITION_TREND_KPI_TYPE)
        .map(item => ({ item, level: getLastAnomalyLevel(item?.values) }))
        .filter(({ level }) => level > 0);

    if (!signalLevels.length) { return []; }

    const maxLevel = Math.max(...signalLevels.map(({ level }) => level));

    return signalLevels
        .filter(({ level }) => level === maxLevel)
        .map(({ item }) => getTranslatedKPINameV2(item));
};

const getStatusBarData = (kpiItem) =>
    (kpiItem?.values ?? []).map(ul => ({
        y: 1,
        x: new Date(ul?.timestamp).getTime(),
        color: statusBarColors[ul?.value],
    }));


export const collectSeriesVisibility = (series, acc) => {
    if (series?.options?.showInLegend !== false && series?.options?.id) {
        acc[series.options.id] = series?.visible;
    }
};

export const collectYAxisVisibility = (axis, visibilityAcc, oppositeAcc) => {
    if (axis?.options?.id) {
        visibilityAcc[axis.options.id] = axis?.visible;
        oppositeAcc[axis.options.id] = axis?.opposite;
    }
};

export const getVisibleSignalAxes = (chart) =>
    chart?.yAxis?.filter(axis => axis?.options?.isSignalYAxis && axis?.visible) ?? [];

export const updateOppositeAxis = (axis, idx) => {
    axis.update({ opposite: idx % 2 !== 0 }, false);
};

export const anySeriesInAxisVisible = (chart, yAxisId, yAxis) =>
    chart?.series?.some(
        s => (s?.options?.yAxis === yAxisId || s?.yAxis === yAxis) &&
            s?.visible &&
            s?.options?.showInLegend !== false
    ) ?? false;


const calculateStatusBarTitleY = (inst) => {
    const totalHeight = inst?.chartHeight || inst?.plotHeight || chartHeightConstants?.min;
    const statusBarTopPixels = 91.5 / 100 * totalHeight;
    const spaceWidth = totalHeight < 1000 ? 20 : 15;
    const centerY = -(totalHeight - statusBarTopPixels - spaceWidth);
    return Math.max(-50, Math.min(-5, centerY));
};

export const getACS1000Series = (conditionTrendsData, chartInstance) => {
    const seriesConfig = { yAxis: [], seriesData: [], allSeriesLength: 0 };

    if (!conditionTrendsData?.length) { return seriesConfig; }

    const signalItems = conditionTrendsData.filter(item => item?.type !== CHART_CONDITION_TREND_KPI_TYPE);
    const initialVisibilityMap = {};
    signalItems.forEach(item => {
        initialVisibilityMap[item?.key] = getLastAnomalyLevel(item?.values) > 0;
    });

    if (!Object.values(initialVisibilityMap).some(Boolean)) {
        const fallback = signalItems.find(i => i?.key?.toLowerCase().includes('conductivity')) || signalItems[0];
        if (fallback) { initialVisibilityMap[fallback?.key] = true; }
    }

    let signalIndex = 0;

    conditionTrendsData.forEach(item => {
        // ── Status bar (Kpi condition trend)
        if (item?.type === CHART_CONDITION_TREND_KPI_TYPE) {
            const statusBarYAxisId = 'acs1000_anomaly_status_bar_yaxis';
            const titleY = calculateStatusBarTitleY(chartInstance);

            seriesConfig.yAxis.push({
                top: '91.5%',
                height: '8%',
                id: statusBarYAxisId,
                title: {
                    text: translate('ABB.Powertrain.Frontend.anomalyOverallStatusBarLabel'),
                    align: 'low',
                    x: 15,
                    y: titleY,
                    style: {
                        fontFamily: PRINT_FONT_FAMILY,
                        color: colors.black,
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '12px',
                        whiteSpace: 'nowrap',
                        transform: 'rotate(0deg)',
                    },
                },
                labels: { enabled: false },
                gridLineWidth: 0,
                lineWidth: 0,
                tickLength: 0,
            });

            seriesConfig.seriesData.push({
                name: CHART_CONDITION_TREND_KPI_TYPE,
                type: 'column',
                id: 'acs1000_anomaly_status_bar_series',
                yAxis: statusBarYAxisId,
                xAxis: 'x',
                data: getStatusBarData(item),
                showInLegend: false,
                enableMouseTracking: false,
                states: { inactive: { enabled: false }, hover: { enabled: false } },
            });

            return;
        }

        const signalColor = getACS1000SignalColor(item?.key);
        const unit = item?.unit || '';
        const convertedUnit = getMeasurementUnitSymbol(item?.unitGroupId);
        const unitLabel = convertedUnit || unit;
        const yAxisId = `y_acs1000_${item?.key}`;
        const seriesId = `acs1000_${item?.key}`;

        const description = getTranslatedKPINameV2(item);
        const lastLevel = getLastAnomalyLevel(item?.values);
        const defaultVisible = initialVisibilityMap[item?.key] ?? false;
        const markerSymbol = getACS1000MarkerSymbol(item?.key, signalIndex);
        signalIndex++;

        const legendName = `${description} (${lastLevel}/${maxAnomalyLevel})`;
        const yAxisTitle = unitLabel ? `${description} (${unitLabel})` : description;

        const lineData = [];
        const anomalyPointData = [];

        forEach(item?.values, measurement => {
            if (measurement?.timestamp !== null) {
                const x = new Date(measurement?.timestamp).getTime();
                const y = measurement?.value;
                lineData.push({ x, y, isAnomalousPoint: measurement?.isAnomalousPoint === 1, anomalyLevel: measurement?.anomalyLevel ?? null });
                if (measurement?.isAnomalousPoint === 1) {
                    anomalyPointData.push({ x, y });
                }
            }
        });

        seriesConfig.yAxis.push({
            top: '0%',
            height: '82%',
            labels: { format: '{value}', style: { color: signalColor } },
            id: yAxisId,
            title: {
                text: yAxisTitle,
                enabled: true,
                style: { color: signalColor },
            },
            visible: defaultVisible,
            isSignalYAxis: true,
        });

        seriesConfig.seriesData.push({
            name: legendName,
            data: lineData,
            tooltip: {
                valueSuffix: unitLabel ? ` ${unitLabel}` : '',
                valueDecimals: 2,
            },
            color: signalColor,
            showInNavigator: true,
            visible: defaultVisible,
            showInLegend: true,
            marker: { enabled: true, radius: 2, symbol: markerSymbol },
            lineWidth: 1,
            id: seriesId,
            yAxis: yAxisId,
            xAxis: 'x',
            enableMouseTracking: true,
            custom: {
                description,
                lastLevel,
                maxAnomalyLevel,
                unitLabel,
                markerSymbol,
            },
        });

        if (anomalyPointData?.length > 0) {
            seriesConfig.seriesData.push({
                name: `${description} ${translate('ABB.Powertrain.Frontend.anomalyPointsLabel')}`,
                type: 'scatter',
                data: anomalyPointData,
                color: ANOMALY_POINT_COLOR,
                marker: {
                    enabled: true,
                    radius: 2,
                    symbol: 'circle',
                    lineWidth: 0,
                },
                linkedTo: seriesId,
                yAxis: yAxisId,
                xAxis: 'x',
                showInLegend: false,
                showInNavigator: false,
                enableMouseTracking: false,
                visible: defaultVisible,
                states: { inactive: { enabled: false }, hover: { enabled: false } },
            });
        }
    });

    const allSeriesLength = seriesConfig.seriesData.reduce(
        (count, s) => count + (s.data?.length ?? 0),
        0
    );

    return { ...seriesConfig, allSeriesLength };
};
